import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import teamService from '../services/teamService';
import crossTabSync from '../utils/crossTabSync';

const AccountContext = createContext(null);

const ACTIVE_ACCOUNT_KEY = 'activeAccountId'; // Must match key used in teamService.js authenticatedFetch

export function AccountProvider({ children }) {
  const queryClient = useQueryClient();
  const [accounts, setAccounts] = useState([]);
  const [activeAccount, setActiveAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Track if this is a cross-tab switch (don't broadcast back)
  const isCrossTabSwitch = useRef(false);

  // Load accounts when authenticated
  const loadAccounts = useCallback(async () => {
    // Check auth state from localStorage since we're outside AppContent
    const token = localStorage.getItem('accessToken');
    if (!token) {
      setAccounts([]);
      setActiveAccount(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const userAccounts = await teamService.getUserAccounts();
      setAccounts(userAccounts);

      if (userAccounts.length > 0) {
        // Try to restore previously selected account
        const savedAccountId = localStorage.getItem(ACTIVE_ACCOUNT_KEY);
        const savedAccount = userAccounts.find(a => a.accountId === savedAccountId);
        
        if (savedAccount) {
          setActiveAccount(savedAccount);
        } else {
          // Default to first account (typically their own account as owner)
          const defaultAccount = userAccounts.find(a => a.isOwner) || userAccounts[0];
          setActiveAccount(defaultAccount);
          localStorage.setItem(ACTIVE_ACCOUNT_KEY, defaultAccount.accountId);
          // FIX: Clear stale subscription cache when account ID changes
          // This prevents showing "subscription required" due to cached status from deleted/invalid account
          localStorage.removeItem('cachedSubscriptionStatus');
          // Stale subscription cache cleared due to account mismatch
        }
      }
    } catch (err) {
      console.error('Failed to load accounts:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAccounts();
  }, [loadAccounts]);

  // Listen for account switches from other tabs
  useEffect(() => {
    const unsubscribe = crossTabSync.onAccountSwitch(({ accountId }) => {
      // Another tab switched accounts - update this tab without full reload
      const account = accounts.find(a => a.accountId === accountId);
      if (account && activeAccount?.accountId !== accountId) {
        isCrossTabSwitch.current = true;
        setActiveAccount(account);
        localStorage.setItem(ACTIVE_ACCOUNT_KEY, accountId);
        
        // Invalidate all queries to refetch with new account context
        queryClient.invalidateQueries();
        
        // Cross-tab account switch detected
        isCrossTabSwitch.current = false;
      }
    });

    return () => unsubscribe();
  }, [accounts, activeAccount, queryClient]);

  // Switch to a different account
  const switchAccount = useCallback(async (accountId, options = {}) => {
    const { skipBroadcast = false, forceReload = false } = options;
    
    const account = accounts.find(a => a.accountId === accountId);
    if (!account) {
      throw new Error('Account not found');
    }

    try {
      // Verify access on backend
      await teamService.switchAccount(accountId);
      
      setActiveAccount(account);
      localStorage.setItem(ACTIVE_ACCOUNT_KEY, accountId);
      
      // Broadcast to other tabs (unless this was triggered by another tab)
      if (!skipBroadcast && !isCrossTabSwitch.current) {
        crossTabSync.broadcastAccountSwitch(accountId, account.accountName);
      }
      
      // Invalidate all queries to refetch with new account context
      // This is cleaner than a full page reload
      if (forceReload) {
        window.location.reload();
      } else {
        queryClient.invalidateQueries();
      }
    } catch (err) {
      console.error('Failed to switch account:', err);
      throw err;
    }
  }, [accounts, queryClient]);

  // Refresh accounts (e.g., after accepting an invite)
  const refreshAccounts = useCallback(async () => {
    await loadAccounts();
  }, [loadAccounts]);

  // Check if user has multiple accounts
  const hasMultipleAccounts = accounts.length > 1;

  // Get current account ID for API calls
  const currentAccountId = activeAccount?.accountId || null;

  const value = {
    accounts,
    activeAccount,
    currentAccountId,
    hasMultipleAccounts,
    loading,
    error,
    switchAccount,
    refreshAccounts,
  };

  return (
    <AccountContext.Provider value={value}>
      {children}
    </AccountContext.Provider>
  );
}

export function useAccount() {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
}

export default AccountContext;
